/**
 * Editor da barrinha do cartão, aberto com duplo clique na barra (ou um toque, na tela de toque),
 * como o `TRExtStatusBarEditorLayout` do original: valor atual e máximo, aceitando "+5", "-3" e
 * "=10" (`barEdit.ts`).
 * - Enter grava e fecha; Esc, "Cancelar" ou clicar fora fecham sem gravar;
 * - ↑ e ↓ trocam de campo;
 * - o campo que não foi mexido fica com o valor de agora, mesmo que mude enquanto o editor está
 *   aberto (outra pessoa ou a ficha mexeu na barra).
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n/index.js';
import { Button, TextEdit } from '../ui/controls.js';
import { barEditResult, type BarValues } from './barEdit.js';

/** largura do editor (px), para caber na tela */
const WIDTH = 220;

/** `onClose(byKeyboard)`: fechou por Enter ou Esc (o foco volta para a barra). */
export function BarEditor({ anchor, name, color, bar, onApply, onClose }: { anchor: DOMRect; name: string; color: string; bar: BarValues | null; onApply: (next: BarValues | null) => void; onClose: (byKeyboard: boolean) => void }) {
  const t = useT();
  const [value, setValue] = useState<string | null>(null);
  const [max, setMax] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: anchor.left, top: anchor.bottom + 4 });

  // embaixo da barra; sem espaço, em cima dela; nunca fora da tela
  useLayoutEffect(() => {
    const h = boxRef.current?.offsetHeight ?? 0;
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - WIDTH - 8));
    const below = anchor.bottom + 4;
    const top = below + h > window.innerHeight - 8 ? Math.max(8, anchor.top - h - 4) : below;
    setPos({ left, top });
  }, [anchor]);

  // clicar fora fecha sem gravar (como o original ao perder o foco)
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) onClose(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [onClose]);

  const apply = (byKeyboard: boolean) => {
    const next = barEditResult(bar, value ?? '', max ?? '');
    if (next !== undefined) onApply(next);
    onClose(byKeyboard);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Enter num botão é o clique dele ("Cancelar" não grava)
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
      e.preventDefault();
      apply(true);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose(true);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const inputs = [...(boxRef.current?.querySelectorAll('input') ?? [])];
      inputs.find((input) => input !== document.activeElement)?.focus();
    }
  };
  const shown = (typed: string | null, current: number | undefined) => typed ?? (current === undefined ? '' : String(current));

  return createPortal(
    <div ref={boxRef} className="rd-barEditor" role="dialog" aria-label={name} style={{ left: pos.left, top: pos.top, width: WIDTH }} onKeyDown={onKeyDown}>
      <div className="rd-barEditor__title">
        <span className="rd-barEditor__swatch" style={{ background: color }} />
        {name}
      </div>
      <div className="rd-barEditor__fields">
        <label>
          <span>{t('users.barEditor.value')}</span>
          <TextEdit look="filled" value={shown(value, bar?.value)} onChange={setValue} inputMode="decimal" maxLength={12} autoFocusOnMount onFocus={(e) => e.currentTarget.select()} aria-label={t('users.barEditor.value')} />
        </label>
        <label>
          <span>{t('users.barEditor.max')}</span>
          <TextEdit look="filled" value={shown(max, bar?.max)} onChange={setMax} inputMode="decimal" maxLength={12} onFocus={(e) => e.currentTarget.select()} aria-label={t('users.barEditor.max')} />
        </label>
      </div>
      <p className="rd-barEditor__hint">{t('users.barEditor.hint')}</p>
      <div className="rd-barEditor__actions">
        <Button variant="secondary" onClick={() => onClose(false)}>
          {t('create.cancel')}
        </Button>
        <Button variant="action" onClick={() => apply(false)}>
          {t('common.ok')}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
